/**
 * Apparaatbeheer: welk toestel ligt bij welke kraam, en hoeveel dagen is het
 * verhuurd geweest.
 *
 * Die dagen zijn geen administratie maar geld: ze bepalen wat er aan kraamsets
 * wordt gefactureerd. Een toestel dat op vrijdag de deur uit gaat en op zondag
 * terugkomt is drie dagen verhuurd, niet twee.
 */

import { vermenigvuldig, type Cent } from './geld.js';
import { evenementdag } from './bonnummer.js';

export type Apparaatstatus = 'op_voorraad' | 'uitgegeven' | 'retour' | 'defect';

export interface Apparaatuitgifte {
  /** Wanneer het toestel is meegegeven, als tijdstip in UTC. */
  uitgegevenOp: string | null;
  /** Wanneer het terug is. Leeg betekent: ligt er nog. */
  retourOp: string | null;
}

/**
 * Het aantal verhuurde dagen, beide dagen meegeteld. Een toestel dat nog niet
 * is uitgegeven levert nul op; een toestel dat nog niet terug is telt door tot
 * vandaag, zodat je tijdens het evenement al ziet wat er loopt.
 */
export function huurdagen(uitgifte: Apparaatuitgifte, nu: Date): number {
  if (uitgifte.uitgegevenOp === null) return 0;

  const begin = evenementdag(new Date(uitgifte.uitgegevenOp));
  const eind = evenementdag(uitgifte.retourOp === null ? nu : new Date(uitgifte.retourOp));
  if (eind < begin) return 0;

  const verschil = Date.parse(`${eind}T12:00:00Z`) - Date.parse(`${begin}T12:00:00Z`);
  return Math.trunc(verschil / 86_400_000) + 1;
}

export function huurkostenCent(dagen: number, dagtariefCent: Cent): Cent {
  return vermenigvuldig(dagtariefCent, dagen);
}

export interface Apparaatregel {
  id: string;
  soort: string;
  serienummer: string | null;
  status: Apparaatstatus;
  kraamnaam: string | null;
  uitgifte: Apparaatuitgifte;
  vervangingswaardeCent: Cent;
}

export interface Apparaatoverzicht {
  totaal: number;
  uitgegeven: number;
  opVoorraad: number;
  defect: number;
  /** Toestellen die nog buiten zijn terwijl het evenement al voorbij is. */
  nietIngeleverd: Apparaatregel[];
  /** Wat er aan vervangingswaarde buiten staat. */
  buitenstaandeWaardeCent: Cent;
}

export function maakApparaatoverzicht(
  apparaten: readonly Apparaatregel[],
  evenementAfgelopen: boolean,
): Apparaatoverzicht {
  const uitgegeven = apparaten.filter((apparaat) => apparaat.status === 'uitgegeven');

  return {
    totaal: apparaten.length,
    uitgegeven: uitgegeven.length,
    opVoorraad: apparaten.filter((apparaat) => apparaat.status === 'op_voorraad').length,
    defect: apparaten.filter((apparaat) => apparaat.status === 'defect').length,
    nietIngeleverd: evenementAfgelopen ? uitgegeven : [],
    buitenstaandeWaardeCent: uitgegeven.reduce(
      (som, apparaat) => som + apparaat.vervangingswaardeCent,
      0,
    ),
  };
}
